import type { Catalog } from '../catalog';
import { Flags, PARAM_TABLE, snap, type NumericParamKey, type SceneParams } from '../sim/params';
import { lang, t, tLightFrom, tParam, toggleLang } from './i18n';

/** Parameters with no slider: not implemented yet, or driven by other controls. */
const HIDDEN = new Set<NumericParamKey>(['horizonTilt', 'lightAuto']);

export interface PanelCallbacks {
  onParam(key: NumericParamKey, value: number): void;
  onPhoto(id: string): void;
  onReseed(): void;
  onShare(): Promise<boolean>;
  onHorizon(): void;
  onPause(): boolean;
  onKiosk(): void;
  onFullscreen(): void;
  onExpert(expert: boolean): void;
  onFlag(flag: number, on: boolean): void;
  onHud(on: boolean): void;
  hudOn(): boolean;
}

const EXPERT_KEY = 'murmurations.expert';

export class Panel {
  readonly root: HTMLElement;
  private expert = false;
  private open = false;
  private paused = false;
  private sliders = new Map<NumericParamKey, HTMLInputElement>();
  private valueLabels = new Map<NumericParamKey, HTMLElement>();
  private flagBoxes = new Map<number, HTMLInputElement>();
  private toast: HTMLElement;
  private toastTimer = 0;
  private pauseBtn: HTMLButtonElement | null = null;
  private toggleBtn: HTMLButtonElement | null = null;
  private modeBtn: HTMLButtonElement | null = null;
  private langBtn: HTMLButtonElement | null = null;

  constructor(
    host: HTMLElement,
    private readonly params: SceneParams,
    private readonly catalog: Catalog,
    private readonly cb: PanelCallbacks,
  ) {
    try {
      this.expert = localStorage.getItem(EXPERT_KEY) === '1';
    } catch {
      /* ignore */
    }
    this.root = document.createElement('div');
    this.root.className = 'panel';
    this.toast = document.createElement('div');
    this.toast.className = 'toast';
    this.toast.setAttribute('role', 'status');
    this.toast.setAttribute('aria-live', 'polite');
    this.toast.hidden = true;
    host.appendChild(this.root);
    host.appendChild(this.toast);
    this.render();
  }

  get isExpert(): boolean {
    return this.expert;
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** Re-read all values from params (after a photo change or a decoded link). */
  refresh(): void {
    for (const spec of PARAM_TABLE) {
      const el = this.sliders.get(spec.key);
      if (el) el.value = String(this.params[spec.key]);
      const lab = this.valueLabels.get(spec.key);
      if (lab) lab.textContent = fmtParam(spec.key, this.params[spec.key], spec.step);
    }
    for (const [flag, box] of this.flagBoxes) box.checked = (this.params.flags & flag) !== 0;
    const sel = this.root.querySelector<HTMLSelectElement>('select.photo');
    if (sel) sel.value = this.params.photo;
    const credit = this.root.querySelector<HTMLElement>('.credit');
    if (credit) this.fillCredit(credit);
  }

  showToast(msg: string, ms = 2200): void {
    this.toast.textContent = msg;
    this.toast.hidden = false;
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (this.toast.hidden = true), ms);
  }

  setOpen(open: boolean): void {
    this.open = open;
    this.root.classList.toggle('open', open);
    document.body.classList.toggle('panel-open', open);
    this.toggleBtn?.setAttribute('aria-expanded', String(open));
  }

  /** Kiosk and hideControls: the whole panel goes away, keys still work. */
  setHidden(hidden: boolean): void {
    this.root.hidden = hidden;
    if (hidden) this.setOpen(false);
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    if (this.pauseBtn) this.pauseBtn.textContent = paused ? t('play') : t('pause');
  }

  /** Rebuild after a language or mode switch; `refocus` names the control that had focus. */
  private render(refocus: 'mode' | 'lang' | null = null): void {
    this.root.replaceChildren();
    this.sliders.clear();
    this.valueLabels.clear();
    this.flagBoxes.clear();

    this.toggleBtn = button('⋯', () => this.setOpen(!this.open), 'toggle');
    this.toggleBtn.setAttribute('aria-label', t('controls'));
    this.toggleBtn.setAttribute('aria-expanded', String(this.open));
    this.root.appendChild(this.toggleBtn);

    const body = document.createElement('div');
    body.className = 'body';
    this.root.appendChild(body);

    const head = document.createElement('div');
    head.className = 'row head';
    const title = document.createElement('span');
    title.className = 'title';
    title.textContent = t('title');
    head.appendChild(title);
    this.modeBtn = button(this.expert ? t('expert') : t('basic'), () => {
      this.expert = !this.expert;
      try {
        localStorage.setItem(EXPERT_KEY, this.expert ? '1' : '0');
      } catch {
        /* ignore */
      }
      this.cb.onExpert(this.expert);
      this.render('mode');
    }, 'small');
    head.appendChild(this.modeBtn);
    this.langBtn = button(t('language'), () => {
      toggleLang();
      this.render('lang');
    }, 'small');
    head.appendChild(this.langBtn);
    body.appendChild(head);

    // Photo picker.
    const photoRow = document.createElement('label');
    photoRow.className = 'row';
    const photoLab = document.createElement('span');
    photoLab.textContent = t('photo');
    photoRow.appendChild(photoLab);
    const sel = document.createElement('select');
    sel.className = 'photo';
    for (const p of this.catalog.photos) {
      const opt = document.createElement('option');
      opt.value = p.id;
      opt.textContent = p.title;
      sel.appendChild(opt);
    }
    sel.value = this.params.photo;
    sel.addEventListener('change', () => this.cb.onPhoto(sel.value));
    photoRow.appendChild(sel);
    body.appendChild(photoRow);

    // Sliders.
    for (const spec of PARAM_TABLE) {
      if (HIDDEN.has(spec.key)) continue;
      if (!this.expert && !spec.basic) continue;
      if (spec.key === 'horizon') continue; // has its own drag mode
      const row = document.createElement('label');
      row.className = 'row slider';
      const name = document.createElement('span');
      name.textContent = tParam(spec.key);
      const val = document.createElement('span');
      val.className = 'val';
      val.textContent = fmtParam(spec.key, this.params[spec.key], spec.step);
      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(spec.min);
      input.max = String(spec.max);
      input.step = String(spec.step);
      input.value = String(this.params[spec.key]);
      input.addEventListener('input', () => {
        const v = snap(spec, Number(input.value));
        val.textContent = fmtParam(spec.key, v, spec.step);
        this.cb.onParam(spec.key, v);
      });
      row.append(name, val, input);
      body.appendChild(row);
      this.sliders.set(spec.key, input);
      this.valueLabels.set(spec.key, val);
    }

    // Expert switches: the two permalink flags nobody could set, and the overlay.
    if (this.expert) {
      body.appendChild(this.checkbox(t('flagLive'), (this.params.flags & Flags.liveAllowed) !== 0, (on) => this.cb.onFlag(Flags.liveAllowed, on), Flags.liveAllowed));
      body.appendChild(this.checkbox(t('flagHideControls'), (this.params.flags & Flags.hideControls) !== 0, (on) => this.cb.onFlag(Flags.hideControls, on), Flags.hideControls));
      body.appendChild(this.checkbox(t('hudToggle'), this.cb.hudOn(), (on) => this.cb.onHud(on)));
    }

    // Actions.
    const actions = document.createElement('div');
    actions.className = 'row actions';
    actions.appendChild(button(t('horizon'), () => this.cb.onHorizon()));
    actions.appendChild(button(t('reseed'), () => this.cb.onReseed()));
    this.pauseBtn = button(this.paused ? t('play') : t('pause'), () => this.setPaused(this.cb.onPause()));
    actions.appendChild(this.pauseBtn);
    actions.appendChild(button(t('kiosk'), () => this.cb.onKiosk()));
    if (document.fullscreenEnabled) actions.appendChild(button(t('fullscreen'), () => this.cb.onFullscreen()));
    const share = button(t('share'), async () => {
      const ok = await this.cb.onShare();
      this.showToast(ok ? t('shared') : t('shareFailed'));
    }, 'primary');
    actions.appendChild(share);
    body.appendChild(actions);

    const credit = document.createElement('div');
    credit.className = 'credit';
    this.fillCredit(credit);
    body.appendChild(credit);

    this.root.classList.toggle('open', this.open);
    // A keyboard user who activated the old button should still be on its replacement.
    if (refocus === 'mode') this.modeBtn.focus();
    if (refocus === 'lang') this.langBtn.focus();
  }

  private checkbox(label: string, checked: boolean, onChange: (on: boolean) => void, flag?: number): HTMLElement {
    const row = document.createElement('label');
    row.className = 'row check';
    const name = document.createElement('span');
    name.textContent = label;
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = checked;
    box.addEventListener('change', () => onChange(box.checked));
    row.append(name, box);
    if (flag !== undefined) this.flagBoxes.set(flag, box);
    return row;
  }

  private fillCredit(credit: HTMLElement): void {
    credit.replaceChildren();
    const photo = this.catalog.photos.find((p) => p.id === this.params.photo);
    if (!photo) return;
    const a = document.createElement('a');
    a.href = photo.source;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = `${t('photoBy')}: ${photo.creator} (${photo.license})`;
    credit.appendChild(a);
  }
}

function button(label: string, onClick: () => void, cls = ''): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  if (cls) b.className = cls;
  b.addEventListener('click', onClick);
  return b;
}

function fmtParam(key: NumericParamKey, v: number, step: number): string {
  if (key === 'lightAngle') return `${Math.round(v)}° · ${tLightFrom(v)}`;
  return fmt(v, step);
}

function fmt(v: number, step: number): string {
  if (step >= 1) return Math.round(v).toLocaleString(lang());
  const digits = Math.max(0, Math.ceil(-Math.log10(step)));
  return v.toFixed(Math.min(digits, 2));
}
